import AgentsCRUDWithFilters from './AgentsCRUDWithFilters';
import AdminUsersCRUD from './AdminUsersCRUD';
import RecommendationWidget from './RecommendationWidget';
import RecommendationsCRUD from './RecommendationsCRUD';
import PersonasCRUD from './PersonasCRUD';
import HomePage from './pages/public/HomePage';
import BrowsePage from './pages/public/BrowsePage';
import Header from './components/common/Header';
import HeroSection from './components/sections/HeroSection';
import TechStackSection from './components/sections/TechStackSection';
import CustomModelsSection from './components/sections/CustomModelsSection';
import AgentCard from './components/common/AgentCard';
import AgentModal from './components/common/AgentModal';
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage';
import TermsOfServicePage from './pages/legal/TermsOfServicePage';
import CookiePolicyPage from './pages/legal/CookiePolicyPage';
import CustomAgentPage from './pages/public/CustomAgentPage';
import Footer from './components/common/Footer';
import ScrollToTop from './components/common/ScrollToTop';
import { Navigate } from 'react-router-dom';

import ContactPage from './pages/public/ContactPage';
import DemoLinksPage from './pages/public/DemoLinksPage';
import AdminContext from './admin/context/AdminContext';
import AgentsCRUD from './admin/components/AgentsCRUD';
import CategoriesCRUD from './admin/components/CategoriesCRUD';
import SubcategoriesCRUD from './admin/components/SubcategoriesCRUD';
import ConfigurationCRUD from './admin/components/ConfigurationCRUD';
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from './lib/queryClient';
import { ToastProvider } from './components/ui/Toast';
import axios from 'axios';
import { 
  Search, 
  Menu, 
  X, 
  Star, 
  Clock, 
  DollarSign, 
  Users, 
  ArrowRight, 
  Play,
  CheckCircle,
  TrendingUp,
  Shield,
  Zap,
  Bot,
  Building,
  CreditCard,
  FileCheck,
  Home,
  BarChart3,
  Settings,
  Loader2,
  Mail,
  Send
} from 'lucide-react';
// AdminPanel is now defined inline below

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Admin Context with Authentication
const AdminDashboard = ({ onLogout }) => {



  const [currentView, setCurrentView] = useState('dashboard');
  const [agents, setAgents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(false);

  const menuItems = [
    { id: 'dashboard', name: 'Dashboard', icon: '🏠' },
    { id: 'agents', name: 'AI Agents', icon: '🤖' },
    { id: 'categories', name: 'Categories', icon: '📁' },
    { id: 'subcategories', name: 'Subcategories', icon: '📂' },
    { id: 'personas', name: 'Personas', icon: '👥' },
    { id: 'config', name: 'Site Configuration', icon: '⚙️' },
    { id: 'admin-users', name: 'Admin Users', icon: '👤' },
    { id: 'recommendations', name: 'Recommendations', icon: '💡' },
  ];

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [agentsRes, categoriesRes, subcategoriesRes, configRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/accelerators?limit=1000`),
        axios.get(`${API_BASE_URL}/api/categories`),
        axios.get(`${API_BASE_URL}/api/subcategories`),
        axios.get(`${API_BASE_URL}/api/config`)
      ]);

      setAgents(agentsRes.data);
      setCategories(categoriesRes.data);
      setSubcategories(subcategoriesRes.data);
      setConfig(configRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  const renderContent = () => {
    switch (currentView) {
      case 'dashboard':
        return <AdminDashboardHome agents={agents} categories={categories} subcategories={subcategories} onViewChange={setCurrentView} />;
      case 'agents':
        return <AgentsCRUDWithFilters agents={agents} categories={categories} subcategories={subcategories} onUpdate={fetchAllData} />;
      case 'categories':
        return <CategoriesCRUD categories={categories} onUpdate={fetchAllData} />;
      case 'personas':
        return <PersonasCRUD />;
      case 'subcategories':
        return <SubcategoriesCRUD subcategories={subcategories} categories={categories} onUpdate={fetchAllData} />;
      case 'config':
        return <ConfigurationCRUD config={config} onUpdate={fetchAllData} />;
      case 'admin-users':
        return <AdminUsersCRUD />;
      case 'recommendations':
        return <RecommendationsCRUD />;
      default:
        return <AdminDashboardHome agents={agents} categories={categories} subcategories={subcategories} onViewChange={setCurrentView} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className="w-64 bg-white shadow-lg">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Bot className="h-8 w-8 text-primary-600" />
            <span className="text-xl font-bold text-gray-900">Admin Panel</span>
          </div>
        </div>
        
        <nav className="mt-6 px-3">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setCurrentView(item.id)}
              className={`w-full flex items-center space-x-3 px-4 py-3 text-left rounded-lg transition-colors mb-1 ${
                currentView === item.id
                  ? 'bg-primary-100 text-primary-700 border-r-2 border-primary-600'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <span className="text-lg">{item.icon}</span>
              <span className="font-medium">{item.name}</span>
            </button>
          ))}
        </nav>

        <div className="absolute bottom-0 w-64 p-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-semibold">SA</span>
              </div>
              <span className="text-sm font-medium text-gray-700">Super Admin</span>
            </div>
            <button
              onClick={onLogout}
              className="text-gray-400 hover:text-red-600 transition-colors"
              title="Logout"
            >
              <span className="text-lg">🚪</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="p-8">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading...</p>
              </div>
            </div>
          ) : (
            renderContent()
          )}
        </div>
      </div>
    </div>
  );
};

const AdminDashboardHome = ({ agents, categories, subcategories, onViewChange }) => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
        <p className="text-gray-600">Welcome to the Ultralender.ai Admin Panel</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div 
          className="bg-white rounded-lg shadow-sm p-6 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => onViewChange('agents')}
        >
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100 text-blue-600">
              <Bot className="h-6 w-6" />
            </div>
            <div className="ml-4">
              <h3 className="text-2xl font-bold text-gray-900">{agents.length}</h3>
              <p className="text-sm text-gray-600">AI Agents</p>
            </div>
          </div>
          <div className="mt-4 text-xs text-blue-600 font-medium">Click to manage →</div>
        </div>

        <div 
          className="bg-white rounded-lg shadow-sm p-6 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => onViewChange('categories')}
        >
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 text-green-600">
              <span className="text-lg">📁</span>
            </div>
            <div className="ml-4">
              <h3 className="text-2xl font-bold text-gray-900">{categories.length}</h3>
              <p className="text-sm text-gray-600">Categories</p>
            </div>
          </div>
          <div className="mt-4 text-xs text-green-600 font-medium">Click to manage →</div>
        </div>

        <div 
          className="bg-white rounded-lg shadow-sm p-6 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => onViewChange('subcategories')}
        >
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100 text-purple-600">
              <span className="text-lg">📂</span>
            </div>
            <div className="ml-4">
              <h3 className="text-2xl font-bold text-gray-900">{subcategories.length}</h3>
              <p className="text-sm text-gray-600">Subcategories</p>
            </div>
          </div>
          <div className="mt-4 text-xs text-purple-600 font-medium">Click to manage →</div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <button 
            onClick={() => onViewChange('agents')}
            className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left transition-colors"
          >
            <div className="text-2xl mb-2">🤖</div>
            <div className="font-medium">Manage Agents</div>
            <div className="text-sm text-gray-600">Add, edit, or delete AI agents</div>
          </button>
          <button 
            onClick={() => onViewChange('categories')}
            className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left transition-colors"
          >
            <div className="text-2xl mb-2">📁</div>
            <div className="font-medium">Manage Categories</div>
            <div className="text-sm text-gray-600">Organize agent categories</div>
          </button>
          <button 
            onClick={() => onViewChange('config')}
            className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left transition-colors"
          >
            <div className="text-2xl mb-2">⚙️</div>
            <div className="font-medium">Site Settings</div>
            <div className="text-sm text-gray-600">Configure website content</div>
          </button>
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left block transition-colors"
          >
            <div className="text-2xl mb-2">🌐</div>
            <div className="font-medium">View Website</div>
            <div className="text-sm text-gray-600">Preview live site</div>
          </a>
        </div>
      </div>

      {/* System Status */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold mb-4">System Status</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-sm font-medium">Database Connection</span>
            </div>
            <span className="text-sm text-green-600 font-medium">Connected</span>
          </div>
          <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-sm font-medium">API Services</span>
            </div>
            <span className="text-sm text-green-600 font-medium">Online</span>
          </div>
          <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span className="text-sm font-medium">Last Updated</span>
            </div>
            <span className="text-sm text-blue-600 font-medium">{new Date().toLocaleString('en-US', { 
              year: 'numeric', 
              month: 'short', 
              day: 'numeric', 
              hour: '2-digit', 
              minute: '2-digit' 
            })}</span>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
        <div className="space-y-3">
          <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <span className="text-sm text-gray-700">System initialized with sample data</span>
            <span className="text-xs text-gray-500 ml-auto">Today</span>
          </div>
          <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-sm text-gray-700">Admin panel accessed</span>
            <span className="text-xs text-gray-500 ml-auto">Now</span>
          </div>
        </div>
      </div>
    </div>
  );
};



const AdminLogin = ({ onLogin }) => {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    const success = onLogin(credentials.username, credentials.password);
    if (!success) {
      setError('Invalid credentials');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center">
            <Bot className="h-12 w-12 text-primary-600" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Admin Access
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Enter your credentials to access the admin panel
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <input
                type="text"
                required
                className="relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Username"
                value={credentials.username}
                onChange={(e) => setCredentials({...credentials, username: e.target.value})}
              />
            </div>
            <div>
              <input
                type="password"
                required
                className="relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Password"
                value={credentials.password}
                onChange={(e) => setCredentials({...credentials, password: e.target.value})}
              />
            </div>
          </div>

          {error && (
            <div className="text-red-600 text-sm text-center">{error}</div>
          )}

          <div>
            <button
              type="submit"
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Sign in
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const App = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [config, setConfig] = useState({});

  useEffect(() => {
    fetchHomeConfig();
  }, []);

  const fetchHomeConfig = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/config`);
      setConfig(response.data);
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  return (
    <ToastProvider>
      <QueryClientProvider client={queryClient}>
        <Router>
          <ScrollToTop />
          <Routes>
            {/* Admin Routes - Clean layout without header/footer */}
            <Route path="/superadmin/*" element={<AdminContext />} />
            
            {/* Public Routes - With header/footer */}
            <Route path="/*" element={
              <div className="min-h-screen bg-gray-50">
                <Header onMenuToggle={() => setIsMenuOpen(!isMenuOpen)} isMenuOpen={isMenuOpen} config={config} />
                <Routes>
                  <Route path="/" element={<Navigate to="/browse" replace />} />
                  <Route path="/browse" element={<BrowsePage />} />
                  <Route path="/categories" element={<BrowsePage />} />
                  <Route path="/personas" element={<BrowsePage />} />
                  <Route path="/custom" element={<CustomAgentPage />} />
                  <Route path="/demo-links" element={<DemoLinksPage />} />
                  <Route path="/contact" element={<ContactPage config={config} />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                  <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                  <Route path="/cookie-policy" element={<CookiePolicyPage />} />
                </Routes>
                <Footer config={config} />
                <RecommendationWidget />
              </div>
            } />
          </Routes>
        </Router>
        
        {/* React Query Devtools - disabled temporarily due to locale issues */}
        {false && process.env.NODE_ENV === 'development' && (
          <ReactQueryDevtools 
            initialIsOpen={false} 
            position="bottom-right"
            buttonPosition="bottom-right"
          />
        )}
      </QueryClientProvider>
    </ToastProvider>
  );
};


export default App;
